import { Button, DatePicker, Dropdown, Form, FormInstance, Input, MenuProps, Row, Select } from "antd";
import { FloatNumberInput } from "../../../../../../../../../../components/FloatNumberInput";
import { useButtonHandlers } from "./hooks/useButtonHandlers";
import { FormButtons } from "../common/FormButtons";
import {
  DATE_FORMAT,
  DISTRIBUTION_DESC_MAX,
  DISBURSEMENTS_METHOD_TYPES,
  DISTRIBUTION_AMOUNT_LIMIT
} from "../../../../../../../../../../lib/constants";
import { useCallback, useEffect, useMemo, MouseEvent, useState } from "react";
import { MoreOutlined } from "@ant-design/icons";
import { useFetchBatches } from "./hooks/useFetchBatches";
import { Rule } from "antd/es/form";
import dayjs from "dayjs";
import { transformToFloat } from "../../../../../../../../../../lib/transformToFloat";
import { CustomerInputSearch } from "../../../../../../../../../../components/CustomerInputSearch";
import { IImpoundSingle } from "../../../../../../../../../../types/Impounds";

const { TextArea } = Input;

type TDistributionFormProps = {
  impoundAccount: IImpoundSingle | null;
  reloadData: () => void;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  parentId: string | null;
  primaryBuyerNumber: number;
  primarySellerNumber: number;
};

export const DistributionForm = ({
  impoundAccount,
  reloadData,
  setIsOpen,
  parentId,
  primaryBuyerNumber,
  primarySellerNumber
}: TDistributionFormProps) => {
  const [form]: FormInstance[] = Form.useForm();

  const [key, setKey] = useState(false);

  const { options, isLoading: isBatchesLoading } = useFetchBatches(form);

  const { isFormDisabled, onCancelBtnClick, onSaveBtnClick } = useButtonHandlers({
    reloadData,
    setIsOpen,
    parentId,
    form,
    options
  });

  const onChange = useCallback(
    (e: MouseEvent<HTMLInputElement>) => {
      const id = e.currentTarget.id;
      form.setFieldValue("payeeNumber", id === "1" ? primaryBuyerNumber : primarySellerNumber);
      setKey(!key);
    },
    [form, primaryBuyerNumber, primarySellerNumber, key]
  );

  const payeeItems: MenuProps["items"] = useMemo(
    () => [
      {
        key: "1",
        label: (
          <div id="1" onClick={onChange}>
            Set Primary Buyer
          </div>
        )
      },
      {
        key: "2",
        label: (
          <div id="2" onClick={onChange}>
            Set Primary Seller
          </div>
        )
      }
    ],
    [onChange]
  );

  const descriptionItems: MenuProps["items"] = useMemo(
    () =>
      impoundAccount?.upcNumbers?.map((upcNumber, index) => ({
        key: index,
        label: (
          <div
            onClick={() => {
              const current = form.getFieldValue("description") ?? "";
              form.setFieldValue("description", `${current}${current ? " " : ""}${upcNumber ?? ""}`);
            }}
          >
            UPC Number ({upcNumber})
          </div>
        )
      })),
    [form, impoundAccount]
  );

  const amountRules = useMemo(() => {
    return [
      {
        required: true,
        validateTrigger: "onSubmit"
      },
      () => ({
        validateTrigger: "onSubmit",
        transform: transformToFloat,
        validator(_: Rule, value: number) {
          const impoundBalance = Number(impoundAccount?.balance ?? 0);
          if (value <= parseFloat(impoundBalance.toFixed(2) ?? "0")) {
            return Promise.resolve();
          }

          return Promise.reject(new Error(DISTRIBUTION_AMOUNT_LIMIT));
        }
      })
    ];
  }, [impoundAccount]);

  useEffect(() => {
    const initialValues = {
      date: dayjs(),
      disbursementMethod: DISBURSEMENTS_METHOD_TYPES[0].value
    };

    form.setFieldsValue(initialValues);
  }, [form]);

  return (
    <Row className="d-flex flex-column">
      <Form
        form={form}
        className="w-100 mt-6 distribution-form"
        layout="vertical"
        requiredMark={false}
        disabled={isFormDisabled}
      >
        <Form.Item
          label="Payment Batch"
          name="batchId"
          className="mb-4"
          rules={[{ required: true, validateTrigger: "onSubmit" }]}
        >
          <Select options={options} disabled={isBatchesLoading || isFormDisabled} />
        </Form.Item>
        <Form.Item
          label="Date"
          name="date"
          className="mb-4"
          rules={[{ type: "date", required: true, validateTrigger: "onSubmit" }]}
        >
          <DatePicker format={DATE_FORMAT} placeholder="MM/DD/YY" />
        </Form.Item>
        <Row className="w-100 d-flex align-flex-start justify-space-between">
          <CustomerInputSearch
            form={form}
            label="Payee"
            name="payeeNumber"
            isFormDisabled={isFormDisabled}
            placeholder="Input Payee#, “Enter” to confirm"
            additionalClass="flex-grow-1"
            key={`${key}-payeeNumber`}
            idItemName="payeeId"
          />
          <Dropdown
            className="ml-2 mt-6"
            menu={{ items: payeeItems }}
            trigger={["click"]}
            placement="bottomRight"
            overlayClassName="payee-dropdown"
          >
            <Button shape="circle" icon={<MoreOutlined />}></Button>
          </Dropdown>
        </Row>
        <FloatNumberInput
          name="amount"
          label="Amount"
          form={form}
          className="w-100 mb-4"
          rules={amountRules}
          includeThousandsSeparator
        />
        <Form.Item label="Method" className="w-100 mb-4" name="disbursementMethod">
          <Select options={DISBURSEMENTS_METHOD_TYPES} />
        </Form.Item>
        <Row className="w-100 d-flex align-flex-start justify-space-between">
          <div className="flex-grow-1 position-relative">
            <Form.Item
              label="Description"
              className="w-100 mb-4"
              name="description"
              rules={[
                {
                  max: 200,
                  message: DISTRIBUTION_DESC_MAX,
                  validateTrigger: "onSubmit"
                }
              ]}
            >
              <TextArea rows={2} style={{ resize: "none" }} />
            </Form.Item>
          </div>

          {descriptionItems && descriptionItems.length > 0 && (
            <Dropdown
              className="ml-2 mt-6"
              menu={{ items: descriptionItems }}
              trigger={["click"]}
              placement="bottomRight"
              overlayClassName="payee-dropdown"
            >
              <Button shape="circle" icon={<MoreOutlined />}></Button>
            </Dropdown>
          )}
        </Row>
      </Form>
      <Row>
        <FormButtons
          id={null}
          onCancelBtnClick={onCancelBtnClick}
          onSaveBtnClick={onSaveBtnClick}
          isDisabled={isFormDisabled}
          createButtonTitle="Create Payment"
        />
      </Row>
    </Row>
  );
};
